import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { formatPct, formatMoney, formatDateTimeSeconds } from '../lib/format';
import type { Asset } from '../lib/types';
import { useT } from '../i18n';

// -------------------------------------------------------------------- gainInk
// THE ONE DEFINITION OF "ZERO HAS NO VERDICT TO GIVE".
//
// `ChangeText` and `ChangeMoney` below already got this right — a three-way
// tone, with zero neutral. Five hand-written call sites did not: the plain
// `v >= 0 ? gain : loss` paints ₹0.00 GREEN, which states "this is a gain" about
// something that has not moved. Colouring a number is a verdict without words —
// the same rule that keeps RSI in neutral ink — and the two worst instances are
// the projection table (asking "what if returns are zero?" turns the WHOLE
// Growth column green) and the tax-loss-harvesting screen, where a break-even
// holding's ₹0.00 shows in the gain colour on the one page whose entire subject
// is finding losses.
//
// Hoisted here rather than copied a sixth time: it lived at the bottom of
// Insights.tsx, written for the capital-gains card, and was never applied to the
// table 320 lines above it in the same file.
export const gainInk = (v: number) =>
  v > 0 ? 'text-gain dark:text-gain-dark'
    : v < 0 ? 'text-loss dark:text-loss-dark'
      : 'text-ink dark:text-slate-200';

// ---------------------------------------------------------------- ChangeText
// PERCENT-only by construction. It used to take a `suffix` prop, which was the
// hole the Desk bug crawled through: formatPct ALREADY appends '%', so
// suffix="%" rendered "+0.01%%" — and, far worse, the same component was being
// handed RUPEE amounts, printing a ₹81,17,048 gain as "+8117048.00%". The sim
// caught it on night 14: "I cannot send my brother-in-law a screen that claims
// my portfolio is up eight million percent." Money goes through ChangeMoney.
export function ChangeText({ value, className = '' }: { value: number; className?: string }) {
  const tone = value > 0 ? 'text-gain dark:text-gain-dark' : value < 0 ? 'text-loss dark:text-loss-dark' : 'text-ink-faint';
  return (
    <span className={`num font-semibold ${tone} ${className}`}>
      {formatPct(value)}
    </span>
  );
}

// Same gain/loss tone as ChangeText, but for an AMOUNT — signed, and currency
// formatted through the F43 display-currency helper.
export function ChangeMoney({ value, className = '' }: { value: number; className?: string }) {
  const tone = value > 0 ? 'text-gain dark:text-gain-dark' : value < 0 ? 'text-loss dark:text-loss-dark' : 'text-ink-faint';
  return (
    <span className={`num font-semibold ${tone} ${className}`}>
      {value > 0 ? '+' : ''}{formatMoney(value)}
    </span>
  );
}

// ---------------------------------------------------------------- SandboxBadge
// Conv-26 (sim, every night): "Simulated data" read as "my own money numbers
// are invented" and dented trust in users' OWN holdings. The badge scopes the
// claim to what is actually simulated — market PRICES — and the tooltip spells
// it out. Keep it honest: prices ARE simulated until a live feed exists; the
// user's holdings and amounts are their own entries.
//
// Follow-up (sim, STILL persistent for numbers-minded users — 28/59 fluent
// vc-decliners, 66 night-8 overall): the AMBER "warning" chip itself read as a
// DANGER flag sitting on a money surface — "something here is wrong". The
// disclosure now sits in a CALM NEUTRAL chip: the honest text is unchanged (and
// still carries the sandbox flag), only the alarm colour is dropped. Amber over
// a user's holdings mis-reads as "your numbers are wrong"; a factual "this price
// is simulated" note belongs in a neutral register, not a caution colour.
export function SandboxBadge({ label = 'Simulated market prices' }: { label?: string }) {
  const t = useT();
  return (
    <span
      // shrink-0 + nowrap (sim night 23): squeezed in a flex row at 390px the chip
      // wrapped a syllable per line — "Si/mu/lat/ed" on Data & Privacy.
      className="chip shrink-0 whitespace-nowrap border border-stone-200 bg-surface-sunken text-ink-soft dark:border-stone-700 dark:bg-surface-dark dark:text-slate-300"
      title={t('Your holdings and amounts are your own entries. Listed-market prices are simulated until a live market feed is connected.')}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-stone-400 dark:bg-stone-500" />
      {t(label)}
    </span>
  );
}

// ---------------------------------------------------------------- PrivateHoldingsNote
// A1: net-worth totals fold in the user's self-reported private investments (F98).
// This single-sources the caveat copy so every folded-total surface discloses the
// same thing — an unverified self-estimate, described (never rated or advised on).
// Renders nothing when there is no private value to disclose.
export function PrivateHoldingsNote({ value, className = '' }: { value?: number; className?: string }) {
  const t = useT();
  if (!value || value <= 0) return null;
  return (
    <p className={`text-xs text-ink-soft dark:text-slate-400 ${className}`}>
      {t('Includes {amount} in self-reported private investments, which Sansara does not value or verify.', {
        // Paise kept on purpose (founder 2026-10-30): exact over rounded.
        amount: formatMoney(value),
      })}
    </p>
  );
}

// ---------------------------------------------------------------- ValueSourceNote
// Conv-34 (sim, the top trust wound on all 12 nights): "I entered my own shop and
// fund values, so those must be mine, but the app calls the prices simulated — I
// would like ONE SENTENCE saying which of my numbers are mine and which are
// invented." That sentence already existed — but ONLY as SandboxBadge's `title=`
// tooltip, which never fires on touch and is invisible to nearly every desktop
// user, so the disclosure was effectively unshipped. Conv-26/28/29 each reworked
// the badge's COLOUR and the complaint survived all three, because the colour was
// never the missing part: the explanation was.
//
// The literal is REUSED VERBATIM from the tooltip on purpose — the English source
// string IS the i18n key, so re-rendering the same bytes costs zero catalog churn
// and inherits the Hindi/Bengali translations that already exist for it.
export function ValueSourceNote({ className = '' }: { className?: string }) {
  const t = useT();
  return (
    <p className={`text-xs text-ink-soft dark:text-slate-400 ${className}`}>
      {t('Your holdings and amounts are your own entries. Listed-market prices are simulated until a live market feed is connected.')}
    </p>
  );
}

// ---------------------------------------------------------------- AsOfNote
// Night 16 (2026-08-03), the #1 ask of BOTH critic cohorts independently: not a
// single headline figure in the app carries an as-of stamp — not the Dashboard
// NET WORTH, the Portfolio TOTAL VALUE, the Insights cards, the Markets index
// cards or the Household totals. The mechanism already existed and was already
// right on ONE screen (Data & Privacy renders `As of {date}` off the provenance
// payload); it simply never travelled. A figure with no time on it silently
// invites the reader to assume "now", which is the one thing a portfolio total
// cannot promise.
//
// Deliberately a COMPONENT, not a copied `<p>`: the same shape as its two
// siblings above, so a future change to how Sansara times its numbers has one
// place to happen. It reuses the EXISTING catalog key `'As of {date}'` verbatim
// — already translated in all nine non-English locales — so this adds no
// translation debt and no `check:i18n-coverage` obligation.
//
// A time (not just a date) on purpose: a net worth restated during the trading
// day is a different number from the one shown this morning. And SECONDS since
// sim night 24: simulated prices move every 5 s, so two screens a few seconds
// apart differ, and a minute stamp made them look like one moment.
export function AsOfNote({ iso, className = '' }: { iso?: string | null; className?: string }) {
  const t = useT();
  if (!iso) return null;
  const d = new Date(iso);
  // An unparseable stamp renders NOTHING rather than "As of Invalid Date".
  // A missing caveat is a gap; a caveat that prints garbage beside a money
  // figure costs trust in the figure — the Conv-34 lesson, in miniature.
  if (Number.isNaN(d.getTime())) return null;
  return (
    <p className={`text-xs text-ink-faint dark:text-slate-400 ${className}`}>
      {t('As of {date}', { date: formatDateTimeSeconds(iso) })}
    </p>
  );
}

// ---------------------------------------------------------------- PriceSourceBadge
// WHERE ONE HOLDING'S VALUE CAME FROM, on the row itself.
//
// The server has decided this in ONE place since Conv-44 (`pricingFor` in
// route-helpers, read by `assetPricing`), and the F18 CSV export already puts a
// per-row source label in a file an accountant opens. The screen was the surface
// that never got it: the Portfolio table branched on `priced === 'market'` alone,
// so an AMFI-NAV-priced fund and a hand-typed value rendered identically, and —
// worse — the "simulated" chip was applied to EVERY market row WITHOUT consulting
// `valuation.sandbox`. That is the Batch-7/F55 trap in client form: the day a live
// quote feed is switched on, the app would go on stamping "simulated" over real
// prices. Read the flag; never re-derive it from `priced`.
//
// The four cases map to the server's own `sourceLabelKey` vocabulary
// (server/portfolio-export.mjs) so the screen and the exported file cannot come
// to different conclusions about the same holding.
//
// ⚠️ `valuation.sandbox` is UNDEFINED — not false — on a manual row, because the
// pricer that feeds `/api/portfolio/assets` omits the key entirely on that
// branch. Branch on `priced` FIRST; a `sandbox === false` test silently misses
// every manual holding.
export function PriceSourceBadge({ valuation }: { valuation?: Asset['valuation'] }) {
  const t = useT();
  if (!valuation) return null;
  if (valuation.priced === 'market') {
    return valuation.sandbox === false
      ? <LiveBadge label={t('Live')} />
      : <SandboxBadge label={t('Simulated')} />;
  }
  // An AMFI NAV is REAL published data, so it is never the sandbox chip — the
  // Conv-27 rule that a NAV-priced fund is `sandbox:false`, reaching the screen.
  if (valuation.priced === 'nav') {
    return (
      <span className="chip border border-stone-200 bg-surface-sunken text-ink-soft dark:border-stone-700 dark:bg-surface-dark dark:text-slate-300">
        {t('AMFI NAV')}
      </span>
    );
  }
  return (
    <span className="chip border border-stone-200 bg-surface-sunken text-ink-soft dark:border-stone-700 dark:bg-surface-dark dark:text-slate-300">
      {t('Your entry')}
    </span>
  );
}

// The legend for the badges above. A chip nobody can decode is decoration, and
// this repo's own rule is that a tooltip is not a disclosure.
export function PriceSourceNote({ className = '' }: { className?: string }) {
  const t = useT();
  return (
    <p className={`text-xs text-ink-soft dark:text-slate-400 ${className}`}>
      {t('Each holding shows where its value came from: a simulated market price, an official AMFI fund NAV, or the figure you entered yourself.')}
    </p>
  );
}

export function LiveBadge({ label = 'Live' }: { label?: string }) {
  const t = useT();
  return (
    <span className="chip border border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400">
      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
      {t(label)}
    </span>
  );
}

// ---------------------------------------------------------------- StatCard
export function StatCard({
  label,
  value,
  sub,
  tone = 'default',
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: 'default' | 'gain' | 'loss';
}) {
  const valueTone =
    tone === 'gain' ? 'text-gain dark:text-gain-dark' : tone === 'loss' ? 'text-loss dark:text-loss-dark' : '';
  return (
    // A4 (density pass): under 640px the three stacked stat cards cost ~330px
    // of vertical scroll before any content on a 390px phone. Below the break
    // the card becomes ONE ROW — label left, value right, sub underneath —
    // which is ~60% shorter with no text, size or colour change (the 12px
    // floor and every contrast pair are untouched).
    <div className="card p-5 max-[640px]:flex max-[640px]:flex-wrap max-[640px]:items-baseline max-[640px]:justify-between max-[640px]:gap-x-3 max-[640px]:p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-ink-faint dark:text-slate-400">{label}</p>
      <p className={`num mt-1.5 font-display text-2xl font-bold max-[640px]:mt-0 ${valueTone}`}>{value}</p>
      {sub ? <div className="mt-1 text-sm text-ink-soft dark:text-slate-400 max-[640px]:mt-0 max-[640px]:basis-full">{sub}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------- Modal
export function Modal({
  open,
  onClose,
  title,
  children,
  wide = false,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  wide?: boolean;
}) {
  const t = useT();
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;
  // PORTALLED to <body>: a Modal rendered inside the app header (z-40, its own
  // stacking context) drew UNDER the phone tab bar (also z-40, later in the DOM)
  // whatever its own z-index — the Ask Sansara sheet lost its bottom rows behind
  // Home/Portfolio/Markets at 390px (sim night 24). Outside the header it is on top.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true">
      <button aria-label={t('Close')} className="absolute inset-0 bg-ink/40 backdrop-blur-sm" onClick={onClose} />
      <div
        className={`rise-in relative z-10 max-h-[88vh] w-full overflow-y-auto rounded-t-2xl bg-surface p-6 shadow-pop dark:bg-surface-dark-raised sm:rounded-2xl ${
          wide ? 'sm:max-w-2xl' : 'sm:max-w-md'
        }`}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold">{title}</h2>
          <button className="btn-ghost -mr-2 rounded-lg p-2" onClick={onClose} aria-label={t('Close dialog')}>
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------- Skeleton + EmptyState
export function SkeletonRows({ rows = 4, height = 'h-14' }: { rows?: number; height?: string }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className={`skeleton w-full ${height}`} />
      ))}
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  body,
  action,
}: {
  icon?: ReactNode;
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className="card flex flex-col items-center px-6 py-12 text-center">
      {icon ? <div className="mb-3 text-ink-faint dark:text-slate-400">{icon}</div> : null}
      <h3 className="font-display text-base font-bold">{title}</h3>
      <p className="mt-1 max-w-sm text-sm text-ink-soft dark:text-slate-400">{body}</p>
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------- Segmented control
//
// TWO LAYOUTS, and the split is deliberate — measured on the deployed build at
// 390×844, Planning's NINE tabs wrapped to FOUR lines and ate ~300px, i.e. 35%
// of the screen, before a single word of content. That is the sharpest instance
// of the sim's `too_many_items` complaint (324/500) and of `meanNavClarity`
// 5.40, the weakest axis of the whole 21-night series.
//
//   • FEW options (< SCROLL_FROM): keep WRAPPING. Conv-40 fixed the plans page
//     this way and that fix stands — a three-option pricing cadence must show
//     all three at once, because an option a buyer cannot see is an option they
//     cannot choose. Never regress this into a scroller.
//   • MANY options (>= SCROLL_FROM): ONE line that scrolls horizontally. The
//     row is a wayfinding strip, not a choice set: the user is looking for one
//     named tool, and a four-line block of pills is what makes them stop
//     looking. `mx-auto w-max` inside an `overflow-x-auto` wrapper centres the
//     row while it fits and scrolls it from the LEFT once it does not — which
//     is what `justify-center` could not do (it clips the left edge, the
//     original Conv-40 bug).
//
// The active pill is scrolled into view on mount and on every change, or a user
// who lands on the ninth tab sees a row that looks scrolled to somebody else's
// selection.
const SCROLL_FROM = 6;

export function Segmented<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: string; badge?: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  const scrolls = options.length >= SCROLL_FROM;
  const activeRef = useRef<HTMLButtonElement | null>(null);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  // Conv-56 fix 11 — a strip that scrolls is only discoverable as a strip if
  // SOMETHING says so. The compare sim filed the clipped last tab ("Cross-b…")
  // 100+ times: the tail was amputated with no chevron, no fade, no scroll hint,
  // so the eleventh destination simply did not exist for a 1280px reader. A
  // right-edge fade plus a chevron appear exactly while there is more to the
  // right, and vanish at the end — measured from the scroller, not assumed.
  const [canScrollRight, setCanScrollRight] = useState(false);
  const updateOverflow = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);
  useEffect(() => {
    if (!scrolls) return;
    updateOverflow();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateOverflow, { passive: true });
    const onResize = () => updateOverflow();
    window.addEventListener('resize', onResize);
    return () => {
      el.removeEventListener('scroll', updateOverflow);
      window.removeEventListener('resize', onResize);
    };
  }, [scrolls, options, updateOverflow]);
  useEffect(() => {
    if (!scrolls) return;
    // `inline: 'nearest'` scrolls the strip only as far as it must, and
    // `block: 'nearest'` keeps it from dragging the PAGE to the control.
    activeRef.current?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
  }, [scrolls, value]);

  const row = (
    <div
      // ONE scrolling line at every width from six options up (founder, 30 Oct
      // 2026). Conv-57 F6 made it wrap below 640px after the rival run said the
      // scroller hid the paid tabs; sim night 23 then measured the cost — Progress
      // (9) and Connect (11) took 4–7 lines before any content, 146 expert + 45
      // rival critics objected and none praised it. The fade + chevron (Conv-56
      // fix 11) is what keeps the tail discoverable; do not re-add the wrap.
      className={`${scrolls
        ? 'mx-auto flex w-max flex-nowrap'
        : 'inline-flex max-w-full flex-wrap justify-center gap-y-1'} rounded-xl border border-slate-200 bg-surface-sunken p-1 dark:border-slate-700 dark:bg-surface-dark`}
    >
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          ref={value === opt.value ? activeRef : undefined}
          onClick={() => onChange(opt.value)}
          className={`rounded-lg px-3.5 py-1.5 text-sm font-semibold transition-colors ${
            value === opt.value
              ? 'bg-surface text-ink shadow-card dark:bg-surface-dark-raised dark:text-slate-100'
              : 'text-ink-soft hover:text-ink dark:text-slate-400 dark:hover:text-slate-200'
          } ${scrolls ? 'whitespace-nowrap' : ''}`}
        >
          {scrolls ? (
            <>
              {opt.label}
              {/* SCROLL MODE keeps the inline chip: the row is one line by
                  construction, so an inline badge costs nothing. */}
              {opt.badge ? (
                <span className="ml-1.5 inline-block whitespace-nowrap rounded-full bg-gain/10 px-1.5 align-middle text-xs font-bold leading-tight text-gain dark:bg-gain-dark/15 dark:text-gain-dark">
                  {opt.badge}
                </span>
              ) : null}
            </>
          ) : (
            <>
              {/* WRAP MODE stacks the badge UNDER the label. The compare sim's
                  findings item 21: with badges inline, the pricing control's
                  third pill ("Yearly · 2 months free") overflowed the row and
                  wrapped ALONE onto a second line, so the three-option control
                  stopped reading as one control. Stacking keeps every pill at
                  label-width, so all options share one line at 390px — which is
                  the property Conv-40's "never a scroller" rule exists to keep
                  (an option a buyer cannot see is an option they cannot choose). */}
              <span className="block whitespace-nowrap">{opt.label}</span>
              {opt.badge ? (
                <span className="mt-0.5 block whitespace-nowrap text-xs font-bold leading-tight text-gain dark:text-gain-dark">
                  {opt.badge}
                </span>
              ) : null}
            </>
          )}
        </button>
      ))}
    </div>
  );

  // The scrollbar is hidden rather than styled: on a phone the strip is swiped,
  // and on a desktop it is wide enough not to scroll at all, so a permanent
  // 15px gutter under the pills would be pure noise. Arbitrary variants keep
  // this local instead of adding a utility to tailwind.config.js.
  return scrolls ? (
    <div className="relative max-w-full">
      <div
        ref={scrollerRef}
        className="max-w-full overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {row}
      </div>
      {/* The affordance the sim asked for by name: a right fade + chevron while
          more tabs sit off-screen, gone at the end. Sits above the strip,
          pointer-events-none so it never eats a tap aimed at a pill.
          Conv-57 fix 4 — the first version drew the chevron ON TOP of the last
          visible label (a stray ">" inside "Your divide…"), and the fade
          blended to `surface` while the strip sits on `surface-sunken`, so the
          whole affordance read as clipping with a smudge. The fade now matches
          the strip's own ground and widens under a solid pad that carries the
          chevron clear of the text. */}
      {canScrollRight ? (
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center gap-0 pr-0.5">
          <span className="h-full w-14 bg-gradient-to-l from-surface-sunken to-transparent dark:from-surface-dark" />
          <span className="flex h-[calc(100%-8px)] w-5 items-center justify-center rounded-md bg-surface-sunken dark:bg-surface-dark">
            <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" className="text-ink-soft dark:text-slate-300">
              <path d="M4 2l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </div>
      ) : null}
    </div>
  ) : (
    row
  );
}

// ---------------------------------------------------------------- Callout
export function Callout({
  tone = 'info',
  title,
  children,
}: {
  tone?: 'info' | 'warn' | 'lock';
  title?: string;
  children: ReactNode;
}) {
  const styles = {
    info: 'border-brand-200 bg-brand-50 text-brand-900 dark:border-brand-500/30 dark:bg-brand-500/10 dark:text-brand-200',
    warn: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200',
    lock: 'border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-600/50 dark:bg-slate-800/50 dark:text-slate-300',
  }[tone];
  return (
    <div className={`rounded-xl border px-4 py-3 text-sm leading-relaxed ${styles}`}>
      {title ? <p className="mb-0.5 font-bold">{title}</p> : null}
      {children}
    </div>
  );
}
